import type { Theme } from './types';

/**
 * A user-created theme: a name plus three seed colors (background, accent,
 * text). The full 13-token `Theme` is derived on demand via `deriveTheme`.
 * Mirrors iOS `EgoDomain.CustomTheme`.
 */
export interface CustomTheme {
  id: string;
  name: string;
  bgHex: string;
  accentHex: string;
  textHex: string;
}

/** sRGB components in 0...1 (mirrors iOS `HexColorComponents`). */
interface Rgb01 {
  r: number;
  g: number;
  b: number;
}

// ── Derivation constants ─────────────────────────────────────────────────
// Mirrors iOS `Derivation` (ThemeDerive.swift) EXACTLY — the only hardcoded
// color numbers outside the built-in palettes.

// Dark backgrounds: surfaces raise toward white.
const DARK_SURFACE_LIFT = 0.03;
const DARK_ELEVATED_LIFT = 0.06;
const DARK_HOVER_LIFT = 0.12;

// Light backgrounds: surfaces raise toward white, hover darkens.
const LIGHT_SURFACE_LIFT = 0.35;
const LIGHT_ELEVATED_LIFT = 0.6;
const LIGHT_HOVER_DROP = 0.04;

const MUTED_BLEND = 0.35;
const DIM_BLEND = 0.35;
const BORDER_BLEND = 0.45;
const EGO_HUE_ROTATION = 180;

const DARK_BORDER_SUBTLE_OPACITY = 0.12;
const LIGHT_BORDER_SUBTLE_OPACITY = 0.1;

/** Constant across all built-ins (themes.ts `blue`). */
const BLUE_HEX = '#3d46c2';

// Dusk seeds — fallbacks for unparseable hex input.
const FALLBACK_BG: Rgb01 = { r: 22 / 255, g: 23 / 255, b: 30 / 255 }; // #16171e
const FALLBACK_ACCENT: Rgb01 = { r: 204 / 255, g: 118 / 255, b: 169 / 255 }; // #cc76a9
const FALLBACK_TEXT: Rgb01 = { r: 235 / 255, g: 235 / 255, b: 235 / 255 }; // #ebebeb

const WHITE: Rgb01 = { r: 1, g: 1, b: 1 };
const BLACK: Rgb01 = { r: 0, g: 0, b: 0 };

// ── sRGB color math (mirrors iOS ColorUtilities.swift) ──────────────────

/**
 * Parses a CSS-style hex color string. Accepts `#RGB`, `#RRGGBB`, and
 * `#RRGGBBAA` (leading `#` optional, case-insensitive); alpha is dropped —
 * theme tokens are opaque hex strings. Mirrors `HexColorComponents(hex:)`.
 */
function parseHex(hex: string): Rgb01 | null {
  let normalized = hex.trim();
  if (normalized.startsWith('#')) normalized = normalized.slice(1);

  // Expand shorthand "abc" -> "aabbcc"
  if (normalized.length === 3) {
    normalized = normalized
      .split('')
      .map((c) => c + c)
      .join('');
  }

  if (normalized.length !== 6 && normalized.length !== 8) return null;
  if (!/^[0-9a-fA-F]+$/.test(normalized)) return null;

  return {
    r: parseInt(normalized.slice(0, 2), 16) / 255,
    g: parseInt(normalized.slice(2, 4), 16) / 255,
    b: parseInt(normalized.slice(4, 6), 16) / 255,
  };
}

/**
 * Linear interpolation between two sRGB colors: `amount` 0 returns `base`,
 * 1 returns `target`. `amount` is clamped to 0...1.
 */
function mix(base: Rgb01, target: Rgb01, amount: number): Rgb01 {
  const t = Math.min(Math.max(amount, 0), 1);
  return {
    r: base.r + (target.r - base.r) * t,
    g: base.g + (target.g - base.g) * t,
    b: base.b + (target.b - base.b) * t,
  };
}

/** WCAG relative luminance (0 = black, 1 = white). */
function relativeLuminance({ r, g, b }: Rgb01): number {
  const linearize = (c: number): number =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  return 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);
}

/** Whether this reads as a dark background (relative luminance below 0.5). */
function isDark(color: Rgb01): boolean {
  return relativeLuminance(color) < 0.5;
}

/**
 * The color with its HSL hue rotated by `degrees` (saturation and lightness
 * preserved). 180° yields the complement — the relationship the built-ins
 * use between `accent` and `egoAccent`. Mirrors `hueRotated(by:)`.
 */
function hueRotated(color: Rgb01, degrees: number): Rgb01 {
  const maxC = Math.max(color.r, color.g, color.b);
  const minC = Math.min(color.r, color.g, color.b);
  const delta = maxC - minC;

  // Achromatic — hue is undefined, rotation is a no-op.
  if (delta <= 0) return color;

  const lightness = (maxC + minC) / 2;
  const saturation =
    lightness > 0.5 ? delta / (2 - maxC - minC) : delta / (maxC + minC);

  let hue: number;
  if (maxC === color.r) {
    hue = (color.g - color.b) / delta + (color.g < color.b ? 6 : 0);
  } else if (maxC === color.g) {
    hue = (color.b - color.r) / delta + 2;
  } else {
    hue = (color.r - color.g) / delta + 4;
  }
  hue /= 6;

  hue = (hue + degrees / 360) % 1;
  if (hue < 0) hue += 1;

  const hueToChannel = (p: number, q: number, t: number): number => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  const q =
    lightness < 0.5
      ? lightness * (1 + saturation)
      : lightness + saturation - lightness * saturation;
  const p = 2 * lightness - q;

  return {
    r: hueToChannel(p, q, hue + 1 / 3),
    g: hueToChannel(p, q, hue),
    b: hueToChannel(p, q, hue - 1 / 3),
  };
}

/** 0...1 channel to a 0...255 byte (clamped, rounded). */
function toByte(c: number): number {
  return Math.round(Math.min(Math.max(c, 0), 1) * 255);
}

/** Lowercase "#rrggbb". */
function toHexString({ r, g, b }: Rgb01): string {
  const pair = (c: number): string => toByte(c).toString(16).padStart(2, '0');
  return `#${pair(r)}${pair(g)}${pair(b)}`;
}

/** "rgba(r, g, b, a)" matching the built-ins' borderSubtle formatting. */
function toRgbaString({ r, g, b }: Rgb01, opacity: number): string {
  return `rgba(${toByte(r)}, ${toByte(g)}, ${toByte(b)}, ${opacity.toFixed(2)})`;
}

// ── Derivation ───────────────────────────────────────────────────────────

/**
 * Derives a full 13-token theme from three seed colors, mirroring the
 * internal relationships of the hand-tuned built-ins (dusk/light/dawn) and
 * iOS `Theme.derive(label:bgHex:accentHex:textHex:)` exactly:
 *
 * - `bgBase` = bg; dark when WCAG relative luminance < 0.5
 * - dark bg: surface/elevated/hover raise +3% / +6% / +12% toward white
 * - light bg: surface +35% / elevated +60% toward white; hover DARKENS
 *   4% toward black (matches light.bgHover < light.bgBase)
 * - `textMuted` = mix(text → accent 35%); `textDim` = mix(muted → bg 35%)
 * - `borderAccent` = mix(accent → bg 45%)
 * - `egoAccent` = accent hue-rotated 180°; `egoBorder` = mix(egoAccent → bg 45%)
 * - `borderSubtle` = text at 12% opacity (10% on light backgrounds)
 * - `blue` = #3d46c2 (constant across all built-ins)
 *
 * Invalid hex seeds fall back to the corresponding dusk seed.
 */
export function deriveTheme(
  label: string,
  bgHex: string,
  accentHex: string,
  textHex: string,
): Theme {
  const bg = parseHex(bgHex) ?? FALLBACK_BG;
  const accent = parseHex(accentHex) ?? FALLBACK_ACCENT;
  const text = parseHex(textHex) ?? FALLBACK_TEXT;

  const dark = isDark(bg);

  let bgSurface: Rgb01;
  let bgElevated: Rgb01;
  let bgHover: Rgb01;
  if (dark) {
    bgSurface = mix(bg, WHITE, DARK_SURFACE_LIFT);
    bgElevated = mix(bg, WHITE, DARK_ELEVATED_LIFT);
    bgHover = mix(bg, WHITE, DARK_HOVER_LIFT);
  } else {
    bgSurface = mix(bg, WHITE, LIGHT_SURFACE_LIFT);
    bgElevated = mix(bg, WHITE, LIGHT_ELEVATED_LIFT);
    bgHover = mix(bg, BLACK, LIGHT_HOVER_DROP);
  }

  const textMuted = mix(text, accent, MUTED_BLEND);
  const textDim = mix(textMuted, bg, DIM_BLEND);
  const borderAccent = mix(accent, bg, BORDER_BLEND);
  const egoAccent = hueRotated(accent, EGO_HUE_ROTATION);
  const egoBorder = mix(egoAccent, bg, BORDER_BLEND);

  return {
    label,
    bgBase: toHexString(bg),
    bgSurface: toHexString(bgSurface),
    bgElevated: toHexString(bgElevated),
    bgHover: toHexString(bgHover),
    textPrimary: toHexString(text),
    textMuted: toHexString(textMuted),
    textDim: toHexString(textDim),
    accent: toHexString(accent),
    borderAccent: toHexString(borderAccent),
    egoAccent: toHexString(egoAccent),
    egoBorder: toHexString(egoBorder),
    borderSubtle: toRgbaString(
      text,
      dark ? DARK_BORDER_SUBTLE_OPACITY : LIGHT_BORDER_SUBTLE_OPACITY,
    ),
    blue: BLUE_HEX,
  };
}
